/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect } from "react";
import { useFormik } from "formik";
import { useNavigate } from "react-router-dom";
import {
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Chip,
  OutlinedInput,
  Box,
} from "@mui/material";
import { FiSave, FiEye } from "react-icons/fi";
import { postValidationSchema } from "../../utils/validationSchemas";
import { CATEGORIES, POPULAR_TAGS } from "../../utils/constants";
import { generateSlug } from "../../utils/helpers";
import RichTextEditor from "../rich-text-editor";
import ImageUpload from "../image-upload";
import PostPreview from "../post-preview";
import { usePostsService } from "../../service/posts/usePostsService";
import { useAuth } from "../../context/AuthContext";

const PostForm = ({ initialData = null, mode = "create", onSuccess }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { uploadImage, addPost, updatePost } = usePostsService();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(
    initialData?.image_url || null
  );
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isImage, setIsImage] = useState(true);
  const formik = useFormik({
    initialValues: {
      title: initialData?.title || "",
      content: initialData?.content || "",
      excerpt: initialData?.excerpt || "",
      categories: initialData?.categories || [],
      tags: initialData?.tags || [],
      slug: initialData?.slug || "",
      is_featured: initialData?.is_featured || false,
      is_trending: initialData?.is_trending || false,
      is_in_banner: initialData?.is_in_banner || false,
    },
    validationSchema: postValidationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        let imageUrl = initialData?.image_url;
        let imageFileName = initialData?.image_file_name;

        // Upload new image if selected
        if (imageFile) {
          const { publicUrl, fileName } = await uploadImage(imageFile);
          imageUrl = publicUrl;
          imageFileName = fileName;
        }

        const postData = {
          ...values,
          image_url: imageUrl,
          image_file_name: imageFileName,
          author_name: user?.email,
          author_user_id: user?.id,
          is_featured: values.is_featured,
          is_trending: values.is_trending,
          is_in_banner: values.is_in_banner,
        };

        if (mode === "edit" && initialData?.id) {
          const updatedPost = await updatePost(initialData.id, postData);
          if (onSuccess) onSuccess(updatedPost);
        } else {
          await addPost(postData);
        }

        navigate("/posts");
      } catch (error) {
        console.error("Error saving post:", error);
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  // Auto-generate slug from title
  useEffect(() => {
    if (formik.values.title && !formik.values.slug) {
      formik.setFieldValue("slug", generateSlug(formik.values.title));
    }
  }, [formik.values.title]);

  const categoryOptions = CATEGORIES.map((cat) => ({
    value: cat.value,
    label: cat.label,
    color: cat.color,
  }));

  const tagOptions = POPULAR_TAGS.map((tag) => ({
    value: tag,
    label: tag,
  }));

  return (
    <div className="max-w-6xl mx-auto p-6 ">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h1 className="text-3xl font-bold mb-8">
          {mode === "edit" ? "Ndrysho postimin" : "Krijo postim të ri"}
        </h1>

        <form onSubmit={formik.handleSubmit} className="space-y-6">
          {/* Title Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Titulli *
            </label>
            <input
              type="text"
              name="title"
              value={formik.values.title}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent
                ${
                  formik.touched.title && formik.errors.title
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              placeholder="Shkruaj titullin e postimit"
            />
            {formik.touched.title && formik.errors.title && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.title}</p>
            )}
          </div>

          {/* Slug Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Segmenti i URL-së *
            </label>
            <input
              type="text"
              name="slug"
              value={formik.values.slug}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent
                ${
                  formik.touched.slug && formik.errors.slug
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              placeholder="segmenti-url-per-postimin"
            />
            {formik.touched.slug && formik.errors.slug && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.slug}</p>
            )}
          </div>

          {/* Excerpt Field */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Përmbledhje e postimit *
            </label>
            <textarea
              name="excerpt"
              value={formik.values.excerpt}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              rows="3"
              className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent
                ${
                  formik.touched.excerpt && formik.errors.excerpt
                    ? "border-red-500"
                    : "border-gray-300"
                }`}
              placeholder="Përshkrim i shkurtër i postimit"
            />
            {formik.touched.excerpt && formik.errors.excerpt && (
              <p className="text-red-500 text-sm mt-1">
                {formik.errors.excerpt}
              </p>
            )}
          </div>

          {/* Content Field with Rich Text Editor */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Përmbajtja
            </label>
            <RichTextEditor
              value={formik.values.content}
              onChange={(value) => formik.setFieldValue("content", value)}
              placeholder="Shkruani përmbajtjen e postimit tuaj këtu..."
              error={formik.touched.content ? formik.errors.content : null}
            />
          </div>

          {/* Image Upload */}
          <div className="flex items-center mb-4">
            <input
              id="default-checkbox"
              type="checkbox"
              checked={isImage}
              onChange={() => setIsImage(!isImage)}
              className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded-sm focus:ring-blue-500 dark:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600"
            />
            <label
              htmlFor="default-checkbox"
              className="ms-2 text-sm font-medium text-gray-900 dark:text-gray-300"
            >
              Shto imazhin
            </label>
          </div>

          {isImage && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Imazhi i përzgjedhur *
              </label>
              <ImageUpload
                onImageSelect={(file) => {
                  setImageFile(file);
                  if (file) {
                    const reader = new FileReader();
                    reader.onloadend = () => {
                      setImagePreview(reader.result);
                    };
                    reader.readAsDataURL(file);
                  } else {
                    setImagePreview(null);
                  }
                }}
                currentImage={initialData?.image_url}
                error={formik.touched.image && formik.errors.image}
              />
            </div>
          )}

          {/* Categories */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="categories-label">Kategoritë *</InputLabel>
            <Select
              labelId="categories-label"
              id="categories"
              multiple
              value={formik.values.categories}
              onChange={(event) => {
                formik.setFieldValue("categories", event.target.value);
              }}
              input={<OutlinedInput label="Kategoritë *" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip
                      key={value}
                      label={
                        categoryOptions.find((opt) => opt.value === value)
                          ?.label || value
                      }
                    />
                  ))}
                </Box>
              )}
            >
              {categoryOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.categories && formik.errors.categories && (
              <p className="text-red-500 text-sm mt-1">
                {formik.errors.categories}
              </p>
            )}
          </FormControl>

          {/* Tags */}
          <FormControl fullWidth sx={{ mb: 2 }}>
            <InputLabel id="tags-label">Etiketat</InputLabel>
            <Select
              labelId="tags-label"
              id="tags"
              multiple
              value={formik.values.tags}
              onChange={(event) => {
                formik.setFieldValue("tags", event.target.value);
              }}
              input={<OutlinedInput label="Etiketat" />}
              renderValue={(selected) => (
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                  {selected.map((value) => (
                    <Chip key={value} label={value} />
                  ))}
                </Box>
              )}
            >
              {tagOptions.map((option) => (
                <MenuItem key={option.value} value={option.value}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
            {formik.touched.tags && formik.errors.tags && (
              <p className="text-red-500 text-sm mt-1">{formik.errors.tags}</p>
            )}
          </FormControl>

          {/* Feature Options */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold mb-4">Opsionet e shfaqjes</h3>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_featured"
                  checked={formik.values.is_featured}
                  onChange={formik.handleChange}
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">
                  Postim i përzgjedhur
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_trending"
                  checked={formik.values.is_trending}
                  onChange={formik.handleChange}
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">
                  Në trend
                </span>
              </label>

              <label className="flex items-center">
                <input
                  type="checkbox"
                  name="is_in_banner"
                  checked={formik.values.is_in_banner}
                  onChange={formik.handleChange}
                  className="mr-2 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <span className="text-sm font-medium text-gray-700">
                  Në baner kryesor
                </span>
              </label>
            </div>
          </div>

          {/* Form Actions */}
          <div className="flex justify-between items-center pt-6 border-t max-sm:flex-col max-sm:gap-4">
            <button
              type="button"
              onClick={() => navigate("/posts")}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors "
            >
              Anulo
            </button>

            <div className="flex gap-3 max-sm:flex-col">
              <button
                type="button"
                onClick={() => setIsPreviewOpen(true)}
                className="px-6 py-2 border border-blue-500 text-blue-500 rounded-lg hover:bg-blue-50 transition-colors flex items-center gap-2"
              >
                <FiEye />
                Shiko paraprakisht
              </button>

              <button
                type="submit"
                disabled={isSubmitting}
                className={`px-6 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors flex items-center gap-2
                  ${isSubmitting ? "opacity-50 cursor-not-allowed" : ""}`}
              >
                <FiSave />
                {isSubmitting
                  ? "Duke ruajtur..."
                  : mode === "edit"
                  ? "Ndrysho Postimin"
                  : "Krijo postim të ri"}
              </button>
            </div>
          </div>
        </form>
      </div>

      {/* Post Preview Modal */}
      <PostPreview
        isOpen={isPreviewOpen}
        onClose={() => setIsPreviewOpen(false)}
        postData={{
          ...formik.values,
          imagePreview: imagePreview,
          author: user?.email,
        }}
      />
    </div>
  );
};

export default PostForm;
